import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

import { LayoutBaseDePagina } from '../../shared/layouts';
import GraficoColuna from '../../shared/forms/GraficoColuna';
import { MensalidadeService, IMensalidade, IResumoMensalidade, TStatusMensalidade } from '../../shared/services/api/controle/MensalidadeService';
import { IComprovanteMensalidadeDados } from './ComprovanteMensalidade';
import { DialogVisualizarComprovante } from './DialogVisualizarComprovante';

const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const statusLabel: Record<string, string> = {
  pago: 'Pago',
  pendente: 'Pendente',
  atrasado: 'Atrasado',
  cancelado: 'Cancelado',
};

const situacaoMensalidade = (m: IMensalidade): string => {
  if (m.status === 'pendente' && dayjs(m.vencimento).isBefore(dayjs(), 'day')) return 'atrasado';
  return m.status;
};

export const ListagemDeMensalidades: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [ano, setAno] = useState(new Date().getFullYear());
  // Tela abre já filtrada no mês atual — não em "Todos os meses".
  const [mesFiltro, setMesFiltro] = useState<number | ''>(new Date().getMonth() + 1);
  const [statusFiltro, setStatusFiltro] = useState<TStatusMensalidade | ''>('');
  const [paginaLancamentos, setPaginaLancamentos] = useState(1);
  const LINHAS_POR_PAGINA_LANCAMENTOS = 8;

  const [resumo, setResumo] = useState<IResumoMensalidade | null>(null);
  const [isLoadingResumo, setIsLoadingResumo] = useState(true);

  const [mensalidades, setMensalidades] = useState<IMensalidade[]>([]);
  const [isLoadingLista, setIsLoadingLista] = useState(true);

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [dialogBaixa, setDialogBaixa] = useState<IMensalidade | null>(null);
  const [dataPagamento, setDataPagamento] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [formaPagamento, setFormaPagamento] = useState('Pix');
  const [isBaixando, setIsBaixando] = useState(false);

  const [comprovanteDados, setComprovanteDados] = useState<IComprovanteMensalidadeDados | null>(null);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const carregarResumo = () => {
    setIsLoadingResumo(true);
    MensalidadeService.resumo(ano).then((result) => {
      setIsLoadingResumo(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setResumo(result);
    });
  };

  const carregarLista = () => {
    setIsLoadingLista(true);
    MensalidadeService.getAll(1, 500, { ano, status: statusFiltro || undefined }).then((result) => {
      setIsLoadingLista(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setMensalidades(result.data);
    });
  };

  useEffect(() => { carregarResumo(); }, [ano]);
  useEffect(() => { carregarLista(); }, [ano, statusFiltro]);
  useEffect(() => { setPaginaLancamentos(1); }, [ano, mesFiltro, statusFiltro]);

  const handleAbrirBaixa = (mensalidade: IMensalidade) => {
    setDataPagamento(dayjs().format('YYYY-MM-DD'));
    setFormaPagamento('Pix');
    setDialogBaixa(mensalidade);
  };

  const handleConfirmarBaixa = () => {
    if (!dialogBaixa) return;
    setIsBaixando(true);
    MensalidadeService.baixa(dialogBaixa.idmensalidade, dataPagamento, formaPagamento).then((result) => {
      setIsBaixando(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setComprovanteDados({
        idmensalidade: dialogBaixa.idmensalidade,
        nomeempresa: dialogBaixa.nomeempresa,
        fantasiaempresa: dialogBaixa.fantasiaempresa,
        mes: dialogBaixa.mes,
        ano: dialogBaixa.ano,
        valor: dialogBaixa.valor,
        datapagamento: dataPagamento,
        formapagamento: formaPagamento,
      });
      setDialogBaixa(null);
      carregarResumo();
      carregarLista();
    });
  };

  const handleApagarMensalidade = (mensalidade: IMensalidade) => {
    if (!window.confirm(`Apagar a mensalidade de ${dayjs().month(mensalidade.mes - 1).format('MMMM')}/${mensalidade.ano} de ${mensalidade.fantasiaempresa || mensalidade.nomeempresa}?`)) return;
    MensalidadeService.deleteById(mensalidade.idmensalidade).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      carregarResumo();
      carregarLista();
    });
  };

  const handleNotificarAtraso = (mensalidade: IMensalidade) => {
    MensalidadeService.notificarAtraso(mensalidade.idmensalidade).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setTms('success');
      setMsg(`Aviso enviado pelo WhatsApp para ${mensalidade.fantasiaempresa || mensalidade.nomeempresa}.`);
      setOpenmsg(true);
      carregarLista();
    });
  };

  const handleReimprimirComprovante = (mensalidade: IMensalidade) => {
    setComprovanteDados({
      idmensalidade: mensalidade.idmensalidade,
      nomeempresa: mensalidade.nomeempresa,
      fantasiaempresa: mensalidade.fantasiaempresa,
      mes: mensalidade.mes,
      ano: mensalidade.ano,
      valor: mensalidade.valor,
      datapagamento: mensalidade.datapagamento || '',
      formapagamento: mensalidade.formapagamento || '',
    });
  };

  const anosDisponiveis = useMemo(() => {
    const atual = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => atual - 2 + i);
  }, []);

  const seriesGrafico = resumo ? [
    { name: 'Pago', data: resumo.pormes.map((m) => m.pago) },
    { name: 'Pendente', data: resumo.pormes.map((m) => m.pendente) },
    { name: 'Impostos', data: resumo.pormes.map((m) => m.imposto) },
    { name: 'Despesas', data: resumo.pormes.map((m) => m.despesa) },
  ] : [];

  const categoriasGrafico = resumo
    ? resumo.pormes.map((m) => dayjs().month(m.mes - 1).format('MMM'))
    : [];

  // Mês "efetivo" de uma mensalidade pra fins de filtro: paga conta pelo mês do
  // pagamento; a vencer/vencida conta pelo mês do vencimento — mesma regra de caixa
  // usada no resumo (ver Resumo.ts no backend).
  const mesEfetivo = (m: IMensalidade) => dayjs(m.status === 'pago' ? m.datapagamento || m.vencimento : m.vencimento).month() + 1;

  const mensalidadesFiltradas = mesFiltro
    ? mensalidades.filter((m) => mesEfetivo(m) === mesFiltro)
    : mensalidades;

  const totalPaginasLancamentos = Math.max(1, Math.ceil(mensalidadesFiltradas.length / LINHAS_POR_PAGINA_LANCAMENTOS));
  const paginaLancamentosAtual = Math.min(paginaLancamentos, totalPaginasLancamentos);
  const mensalidadesDaPagina = mensalidadesFiltradas.slice(
    (paginaLancamentosAtual - 1) * LINHAS_POR_PAGINA_LANCAMENTOS,
    paginaLancamentosAtual * LINHAS_POR_PAGINA_LANCAMENTOS,
  );

  const vencidasDoMes = mesFiltro
    ? (resumo?.vencidas || []).filter((v) => dayjs(v.vencimento).month() + 1 === mesFiltro)
    : (resumo?.vencidas || []);

  const dadosDoMes = mesFiltro ? resumo?.pormes[mesFiltro - 1] : undefined;
  const recebidoCard = mesFiltro ? (dadosDoMes?.pago || 0) : (resumo?.totalpagoano || 0);
  const pendenteCard = mesFiltro ? (dadosDoMes?.pendente || 0) : (resumo?.totalpendenteano || 0);
  const impostoCard = mesFiltro ? (dadosDoMes?.imposto || 0) : (resumo?.totalimpostoano || 0);
  const atrasadoCard = mesFiltro
    ? vencidasDoMes.reduce((soma, v) => soma + Number(v.valor), 0)
    : (resumo?.totalatrasado || 0);
  const qtdAtrasadasCard = mesFiltro ? vencidasDoMes.length : (resumo?.qtdatrasadas || 0);
  const saldoCard = mesFiltro ? (dadosDoMes?.saldo || 0) : (resumo?.saldoano || 0);

  const rotuloPeriodo = mesFiltro ? `${dayjs().month(mesFiltro - 1).format('MMMM')}/${ano}` : String(ano);

  return (
    <LayoutBaseDePagina
      titulo='Dashboard'
      subtitulo='Visão geral de mensalidades, impostos e despesas do painel de controle.'
      acao={
        <Box display='flex' gap={1}>
          <TextField
            select
            size='small'
            label='Mês'
            value={mesFiltro}
            onChange={(e) => setMesFiltro(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value=''>Todos os meses</MenuItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>{dayjs().month(m - 1).format('MMMM')}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size='small'
            label='Ano'
            value={ano}
            onChange={(e) => setAno(Number(e.target.value))}
            sx={{ minWidth: 120 }}
          >
            {anosDisponiveis.map((a) => (
              <MenuItem key={a} value={a}>{a}</MenuItem>
            ))}
          </TextField>
        </Box>
      }
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 6000}
        onClose={handleCloseMsg}
      >
        <Alert onClose={handleCloseMsg} severity={tms} variant='filled' sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: '12px' }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'capitalize' }}>Recebido em {rotuloPeriodo}</Typography>
            <Typography variant='h5' fontWeight={700} color='success.main'>
              {formatarMoeda(recebidoCard)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: '12px' }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'capitalize' }}>Pendente em {rotuloPeriodo}</Typography>
            <Typography variant='h5' fontWeight={700} color='warning.main'>
              {formatarMoeda(pendenteCard)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: '12px' }}>
            <Typography variant='caption' color='text.secondary'>Atrasado ({qtdAtrasadasCard})</Typography>
            <Typography variant='h5' fontWeight={700} color='error.main'>
              {formatarMoeda(atrasadoCard)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: '12px' }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'capitalize' }}>Impostos pagos em {rotuloPeriodo}</Typography>
            <Typography variant='h5' fontWeight={700} color='text.secondary'>
              {formatarMoeda(impostoCard)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ p: 2, borderRadius: '12px' }}>
            <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'capitalize' }}>Saldo em {rotuloPeriodo} (mensalidades - despesas)</Typography>
            <Typography variant='h5' fontWeight={700} color={saldoCard >= 0 ? 'success.main' : 'error.main'}>
              {formatarMoeda(saldoCard)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', height: '100%' }}>
            <Typography variant='subtitle1' fontWeight={700} gutterBottom>Pago x Pendente x Impostos x Despesas por mês</Typography>
            {!isLoadingResumo && resumo && (
              <GraficoColuna
                series={seriesGrafico}
                options={{ xaxis: { categories: categoriasGrafico }, colors: [theme.palette.success.main, theme.palette.warning.main, theme.palette.text.secondary, theme.palette.error.main], theme: { mode: theme.palette.mode } }}
              />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', height: '100%' }}>
            <Box display='flex' alignItems='center' gap={1} mb={1}>
              <WarningAmberRoundedIcon color='error' fontSize='small' />
              <Typography variant='subtitle1' fontWeight={700}>Mensalidades vencidas</Typography>
            </Box>
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              {vencidasDoMes.map((v) => (
                <Box
                  key={v.idmensalidade}
                  onClick={() => navigate(`/empresas/${v.idempresa}`)}
                  sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    py: 1, px: 1.5, mb: 0.75, borderRadius: '8px', cursor: 'pointer',
                    bgcolor: alpha(theme.palette.error.main, 0.06),
                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12) },
                  }}
                >
                  <Box minWidth={0}>
                    <Typography fontSize='0.8125rem' fontWeight={600} noWrap>{v.fantasiaempresa || v.nomeempresa}</Typography>
                    <Typography fontSize='0.7rem' color='text.secondary'>
                      Venceu em {dayjs(v.vencimento).format('DD/MM/YYYY')} • {v.diasatraso} dia{v.diasatraso !== 1 ? 's' : ''} de atraso
                    </Typography>
                  </Box>
                  <Typography fontSize='0.8125rem' fontWeight={700} color='error.main'>{formatarMoeda(v.valor)}</Typography>
                </Box>
              ))}
              {resumo && vencidasDoMes.length === 0 && (
                <Typography color='text.secondary' fontSize='0.85rem' sx={{ py: 2, textAlign: 'center' }}>
                  Nenhuma mensalidade vencida. 🎉
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 2, borderRadius: '12px' }}>
        <Box display='flex' alignItems='center' justifyContent='space-between' mb={1.5} flexWrap='wrap' gap={1}>
          <Typography variant='subtitle1' fontWeight={700} sx={{ textTransform: 'capitalize' }}>Lançamentos de {rotuloPeriodo}</Typography>
          <TextField
            select
            size='small'
            label='Status'
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value as TStatusMensalidade | '')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value=''>Todos</MenuItem>
            <MenuItem value='pendente'>Pendente</MenuItem>
            <MenuItem value='atrasado'>Atrasado</MenuItem>
            <MenuItem value='pago'>Pago</MenuItem>
            <MenuItem value='cancelado'>Cancelado</MenuItem>
          </TextField>
        </Box>

        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Competência</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell align='right'>Valor</TableCell>
                <TableCell align='center'>Status</TableCell>
                <TableCell align='right'>Ação</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mensalidadesDaPagina.map((m) => {
                const situacao = situacaoMensalidade(m);
                return (
                  <TableRow key={m.idmensalidade} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/empresas/${m.idempresa}`)}>
                    <TableCell>{m.fantasiaempresa || m.nomeempresa}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{dayjs().month(m.mes - 1).format('MMMM')}/{m.ano}</TableCell>
                    <TableCell>{dayjs(m.vencimento).format('DD/MM/YYYY')}</TableCell>
                    <TableCell align='right'>{formatarMoeda(m.valor)}</TableCell>
                    <TableCell align='center'>
                      <Chip
                        label={statusLabel[situacao]}
                        size='small'
                        color={situacao === 'pago' ? 'success' : situacao === 'atrasado' ? 'error' : situacao === 'cancelado' ? 'default' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                      {m.status === 'pendente' && (
                        <Button size='small' onClick={() => handleAbrirBaixa(m)}>Dar baixa</Button>
                      )}
                      {situacao === 'atrasado' && (
                        <IconButton
                          size='small'
                          onClick={() => handleNotificarAtraso(m)}
                          title={m.whatsappenviadoem ? `Reenviar aviso (último envio ${dayjs(m.whatsappenviadoem).format('DD/MM/YYYY HH:mm')})` : 'Enviar aviso pelo WhatsApp'}
                          sx={{ color: m.whatsappenviadoem ? 'text.disabled' : 'success.main' }}
                        >
                          <WhatsAppIcon fontSize='small' />
                        </IconButton>
                      )}
                      {m.status === 'pago' && (
                        <IconButton size='small' onClick={() => handleReimprimirComprovante(m)} title='Reimprimir comprovante'>
                          <PrintRoundedIcon fontSize='small' />
                        </IconButton>
                      )}
                      <IconButton size='small' onClick={() => handleApagarMensalidade(m)} title='Apagar mensalidade'>
                        <DeleteOutlineRoundedIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!isLoadingLista && mensalidadesFiltradas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align='center'>
                    <Typography color='text.secondary' fontSize='0.85rem' sx={{ py: 2 }}>
                      Nenhum lançamento encontrado para esse filtro.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {mensalidadesFiltradas.length > 0 && (
          <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5} sx={{ mt: 1.5, px: 0.5 }}>
            <Typography variant='caption' color='text.secondary'>
              Mostrando {(paginaLancamentosAtual - 1) * LINHAS_POR_PAGINA_LANCAMENTOS + 1} a {Math.min(paginaLancamentosAtual * LINHAS_POR_PAGINA_LANCAMENTOS, mensalidadesFiltradas.length)} de {mensalidadesFiltradas.length} lançamento{mensalidadesFiltradas.length !== 1 ? 's' : ''}
            </Typography>
            {totalPaginasLancamentos > 1 && (
              <Pagination
                page={paginaLancamentosAtual}
                count={totalPaginasLancamentos}
                onChange={(_, novaPagina) => setPaginaLancamentos(novaPagina)}
                size='small'
                shape='rounded'
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            )}
          </Box>
        )}
      </Paper>

      <Dialog open={!!dialogBaixa} onClose={() => setDialogBaixa(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Dar baixa na mensalidade</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary'>
                {dialogBaixa?.fantasiaempresa || dialogBaixa?.nomeempresa}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type='date'
                label='Data do pagamento'
                InputLabelProps={{ shrink: true }}
                value={dataPagamento}
                onChange={(e) => setDataPagamento(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label='Forma de pagamento' value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)}>
                <MenuItem value='Pix'>Pix</MenuItem>
                <MenuItem value='Boleto'>Boleto</MenuItem>
                <MenuItem value='Cartão'>Cartão</MenuItem>
                <MenuItem value='Transferência'>Transferência</MenuItem>
                <MenuItem value='Dinheiro'>Dinheiro</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogBaixa(null)}>Cancelar</Button>
          <Button variant='contained' disabled={isBaixando} onClick={handleConfirmarBaixa}>Confirmar pagamento</Button>
        </DialogActions>
      </Dialog>

      <DialogVisualizarComprovante
        open={!!comprovanteDados}
        dados={comprovanteDados}
        onClose={() => setComprovanteDados(null)}
      />
    </LayoutBaseDePagina>
  );
};
